/**
 * Record-level visibility for start_collection_contact_flow — no accounting side effects.
 */

import { isReceivableFinanceState } from '@domain/finance/finance-receivables-query';
import type { UniversalBaseEntity } from '@shared/types/entity';
import type { WorkbenchAction } from './action-types';
import { resolveArEntryIdFromWorkbenchEntity } from './resolve-ar-entry-id-from-workbench-entity';
import { COLLECTION_DOMAIN_ACTION_IDS } from './workbench-domain-action-handler-refs';

const COLLECTION_CONTACT_ACTION_DOMAINS = new Set(['collection', 'ar_collection', 'finance_state']);

function readBusinessDomain(record: UniversalBaseEntity | Record<string, unknown>): string {
  const raw = (record as Record<string, unknown>).businessDomain;
  return typeof raw === 'string' ? raw.trim() : '';
}

/** True when the row may expose collection contact open (AR-linked, receivable where applicable). */
export function isCollectionContactFlowRowEligible(
  record: UniversalBaseEntity | Record<string, unknown>
): boolean {
  const businessDomain = readBusinessDomain(record);
  if (!COLLECTION_CONTACT_ACTION_DOMAINS.has(businessDomain)) {
    return false;
  }

  if (
    businessDomain === 'finance_state' &&
    !isReceivableFinanceState(record as UniversalBaseEntity)
  ) {
    return false;
  }

  return resolveArEntryIdFromWorkbenchEntity(record as UniversalBaseEntity) !== null;
}

export function filterCollectionContactFlowWorkbenchActions<T extends WorkbenchAction>(
  actions: readonly T[],
  record: UniversalBaseEntity | Record<string, unknown>
): T[] {
  if (isCollectionContactFlowRowEligible(record)) {
    return [...actions];
  }
  return actions.filter(
    (action) => action.id !== COLLECTION_DOMAIN_ACTION_IDS.startCollectionContactFlow
  );
}
